import { FC, useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { SearchFilter, SearchFilterType, RsqlOperator, FilterOption } from '@mobileTypes'
import { RSQL_OPERATOR_OPTIONS } from '@mobileConstants'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  IconButton,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'

interface FilterConfigDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  filterType?: SearchFilterType
  initialData?: Partial<SearchFilter>
  onClose: () => void
  onSave: (filter: SearchFilter) => void
}

export const FilterConfigDialog: FC<FilterConfigDialogProps> = ({
  open,
  mode,
  filterType,
  initialData,
  onClose,
  onSave,
}) => {
  const [config, setConfig] = useState<Partial<SearchFilter>>(() => ({
    id: initialData?.id || uuidv4(),
    type: filterType || initialData?.type || 'SEARCHBAR',
    label: initialData?.label || '',
    fieldCode: initialData?.fieldCode || '',
    operator: initialData?.operator || '==',
    order: initialData?.order || 0,
    config: initialData?.config || {},
  }))

  useEffect(() => {
    if (open) {
      setConfig({
        id: initialData?.id || uuidv4(),
        type: filterType || initialData?.type || 'SEARCHBAR',
        label: initialData?.label || '',
        fieldCode: initialData?.fieldCode || '',
        operator: initialData?.operator || '==',
        order: initialData?.order || 0,
        config: initialData?.config || {},
      })
    }
  }, [open, filterType, initialData])

  const handleSave = () => {
    if (!config.label || !config.fieldCode || !config.operator || !config.type) {
      return
    }

    onSave(config as SearchFilter)
  }

  const handleAddOption = () => {
    const options = config.config?.options || []
    setConfig({
      ...config,
      config: {
        ...config.config,
        options: [...options, { label: '', value: '' }],
      },
    })
  }

  const handleUpdateOption = (index: number, field: 'label' | 'value', value: string) => {
    const options = [...(config.config?.options || [])]
    options[index] = { ...options[index], [field]: value }
    setConfig({
      ...config,
      config: {
        ...config.config,
        options,
      },
    })
  }

  const handleDeleteOption = (index: number) => {
    const options = config.config?.options || []
    setConfig({
      ...config,
      config: {
        ...config.config,
        options: options.filter((_, i) => i !== index),
      },
    })
  }

  const handleAddDayOffset = () => {
    const dayOffsets = config.config?.dayOffsets || []
    setConfig({
      ...config,
      config: {
        ...config.config,
        dayOffsets: [...dayOffsets, 0],
      },
    })
  }

  const handleUpdateDayOffset = (index: number, value: number) => {
    const dayOffsets = [...(config.config?.dayOffsets || [])]
    dayOffsets[index] = value
    setConfig({
      ...config,
      config: {
        ...config.config,
        dayOffsets,
      },
    })
  }

  const handleDeleteDayOffset = (index: number) => {
    const dayOffsets = config.config?.dayOffsets || []
    setConfig({
      ...config,
      config: {
        ...config.config,
        dayOffsets: dayOffsets.filter((_, i) => i !== index),
      },
    })
  }

  const renderTypeSpecificFields = () => {
    switch (config.type) {
      case 'SEARCHBAR':
        return (
          <TextField
            fullWidth
            label='Placeholder'
            value={config.config?.placeholder || ''}
            onChange={e =>
              setConfig({
                ...config,
                config: { ...config.config, placeholder: e.target.value },
              })
            }
          />
        )

      case 'SELECT':
      case 'MULTISELECT':
      case 'TOGGLE_BUTTONS':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant='subtitle2'>Options</Typography>
              <Button startIcon={<AddIcon />} size='small' onClick={handleAddOption}>
                Add Option
              </Button>
            </Box>
            {(config.config?.options || []).map((option, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label='Label'
                  size='small'
                  value={option.label}
                  onChange={e => handleUpdateOption(index, 'label', e.target.value)}
                />
                <TextField
                  label='Value'
                  size='small'
                  value={option.value}
                  onChange={e => handleUpdateOption(index, 'value', e.target.value)}
                />
                <IconButton size='small' color='error' onClick={() => handleDeleteOption(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )

      case 'DAY_OFFSET':
        return (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant='subtitle2'>Day Offsets</Typography>
              <Button startIcon={<AddIcon />} size='small' onClick={handleAddDayOffset}>
                Add Day Offset
              </Button>
            </Box>
            {(config.config?.dayOffsets || []).map((offset, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  label='Day Offset'
                  type='number'
                  size='small'
                  value={offset}
                  onChange={e => handleUpdateDayOffset(index, parseInt(e.target.value) || 0)}
                />
                <IconButton size='small' color='error' onClick={() => handleDeleteDayOffset(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )

      case 'CHECKBOX':
        return (
          <TextField
            fullWidth
            label='Default Value'
            value={config.config?.defaultValue || ''}
            onChange={e =>
              setConfig({
                ...config,
                config: { ...config.config, defaultValue: e.target.value },
              })
            }
          />
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogTitle>{mode === 'create' ? 'Add Filter' : 'Edit Filter'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label='Filter Label'
              value={config.label || ''}
              onChange={e => setConfig({ ...config, label: e.target.value })}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label='Field Code'
              value={config.fieldCode || ''}
              onChange={e => setConfig({ ...config, fieldCode: e.target.value })}
              required
              helperText='Object field to filter on'
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Operator</InputLabel>
              <Select
                value={config.operator || '=='}
                label='Operator'
                onChange={e => setConfig({ ...config, operator: e.target.value as RsqlOperator })}
              >
                {RSQL_OPERATOR_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            {renderTypeSpecificFields()}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={!config.label || !config.fieldCode || !config.operator}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}





